import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const DistributionAreasPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, data: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DistributionNetworkApi.getAreas({
        companyId: companyId || undefined,
        includeInactive: true,
      });
      if (!res.ok) {
        toast.error(res.message || "خطا در دریافت امور برق");
        setRows([]);
        return;
      }
      setRows(
        (res.data || []).map((r) => ({
          ...r,
          coverageText: (r.cities || []).map((c) => c.title).join("، "),
        }))
      );
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    DistributionNetworkApi.getCompanyLookups().then((r) =>
      setCompanies(r.data || [])
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = !term
    ? rows
    : rows.filter((r) =>
        `${r.name || ""} ${r.code || ""} ${r.countyName || ""} ${r.city || ""} ${r.coverageText || ""} ${r.companyName || ""}`
          .toLowerCase()
          .includes(term.toLowerCase())
      );

  const headCells = [
    { id: "updateOn", label: "بروزرسانی", type: "date" },
    { id: "companyName", label: "شرکت توزیع" },
    { id: "name", label: "نام امور برق" },
    { id: "code", label: "کد" },
    {
      id: "coverageText",
      label: "شهرهای تحت پوشش",
      render: (v) => v || "ثبت نشده",
    },
    { id: "managerName", label: "مدیر" },
    { id: "statusTitle", label: "وضعیت" },
  ];

  const confirmedDelete = async () => {
    const res = await DistributionNetworkApi.deleteArea(deleteConfirm.data.id);
    return {
      isSuccess: res.ok,
      message: res.message || (res.ok ? "حذف شد" : "خطا در حذف"),
    };
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
        <DeleteConfirm
          message={`برای حذف "${deleteConfirm.data.name || ""}" اطمینان دارید؟`}
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({ open: false, data: {} });
            if (loadAgain) load();
          }}
          onSubmit={confirmedDelete}
        />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title="امور برق شرکت‌های توزیع"
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Link to="/SLN/Management/DistributionAreas/create">
                  <Button variant="contained" startIcon={<AddIcon />}>
                    ایجاد
                  </Button>
                </Link>
                <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 1 }} />
                <IconButton color="primary" onClick={load} disabled={loading}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Box display="flex" gap={2} mb={2} flexWrap="wrap">
              <TextField
                select
                size="small"
                label="شرکت توزیع"
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="">همه</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="جستجو"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                sx={{ minWidth: 200 }}
              />
            </Box>
            <EnhancedTable
              rowKey="id"
              rows={filtered}
              headCells={headCells}
              pageSize={100}
              loading={loading}
              actions={[
                {
                  icon: <EditIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "ویرایش",
                  onClick: (row) =>
                    navigate(`/SLN/Management/DistributionAreas/${row.id}`),
                },
                {
                  icon: <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />,
                  tooltip: "حذف",
                  onClick: (row) => setDeleteConfirm({ open: true, data: row }),
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionAreasPage;
